/**
 * Splits a cart line's server-derived display string into a clean
 * descriptive NAME and a separate PART NUMBER (UID-131 design-fidelity fix
 * — the prototype's line table has "Part" and "Requirements" as two
 * separate columns; the name must never repeat the part number).
 *
 * FALLBACK ONLY (UID-131 follow-up — the earlier contract gap is CLOSED):
 * `CartLinePresentation` now carries distinct `displayName`/`partNumber` read-side
 * fields, so `CartLineRow` renders those directly and no longer needs to parse the
 * pre-joined `partId`. This splitter remains as the fallback for a label-less line
 * (`partId` = just the sourceable id / the `Item <id>` placeholder) and for an older
 * feed that sends only the joined `partId` string.
 *
 * Parses a trailing `" (PARTNUMBER)"` group off the end of the string.
 * Never fabricates a part number that isn't actually present in the source
 * string — falls back to treating the whole string as the name with no
 * part number when it doesn't match that shape (e.g. a custom part with no
 * catalog SKU, or the `Item <id>` fallback used when a line has no
 * presentation at all).
 */
export function splitPartIdDisplay(partId: string): {
  readonly name: string
  readonly partNumber: string | null
} {
  const match = /^(.*)\s\(([^()]+)\)$/.exec(partId.trim())
  const name = match?.[1]?.trim()
  const partNumber = match?.[2]?.trim()
  if (!name || !partNumber) {
    return { name: partId, partNumber: null }
  }
  return { name, partNumber }
}
